import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";

/**
 * Landing page for Supabase Auth recovery links (`/reset-password` or `/recover-password`).
 * Session comes from the URL (PKCE `code`, implicit hash, or existing storage) after Supabase redirects here.
 */
export function RecoverPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const id = window.setTimeout(() => {
      navigate("/", { replace: true });
    }, 1200);
    return () => window.clearTimeout(id);
  }, [saved, navigate]);

  useEffect(() => {
    let cancelled = false;

    async function establishSession(): Promise<void> {
      setChecking(true);
      setSessionError(null);

      const cleanUrlToPath = (): void => {
        window.history.replaceState(null, "", `${window.location.pathname}`);
      };

      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get("code");
      if (code) {
        const { error: exchangeErr } = await supabase.auth.exchangeCodeForSession(window.location.href);
        if (cancelled) return;
        if (!exchangeErr) {
          cleanUrlToPath();
          setReady(true);
          setChecking(false);
          return;
        }
        const {
          data: { session: afterFailSession },
        } = await supabase.auth.getSession();
        if (cancelled) return;
        if (afterFailSession) {
          cleanUrlToPath();
          setReady(true);
          setChecking(false);
          return;
        }
        setSessionError(exchangeErr.message || "This reset link is invalid or has expired.");
        setChecking(false);
        return;
      }

      const hash = window.location.hash.replace(/^#/, "");
      if (hash) {
        const params = new URLSearchParams(hash);
        const type = params.get("type");
        const access_token = params.get("access_token");
        const refresh_token = params.get("refresh_token");
        if (type === "recovery" && access_token && refresh_token) {
          const { error: setErr } = await supabase.auth.setSession({ access_token, refresh_token });
          if (cancelled) return;
          if (setErr) {
            setSessionError(setErr.message);
            setChecking(false);
            return;
          }
          setReady(true);
          setChecking(false);
          window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
          return;
        }
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled) return;
      if (session) {
        setReady(true);
      } else {
        setSessionError(
          "This reset link is invalid or has expired. Use “Forgot password” on the sign-in page or ask your administrator to send a new reset email.",
        );
      }
      setChecking(false);
    }

    void establishSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === "PASSWORD_RECOVERY" && session) {
        setReady(true);
        setSessionError(null);
        setChecking(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!ready) {
      setError("Your reset session is not ready yet. Refresh the page or open the link from your email again.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) {
        setError(updErr.message);
        return;
      }
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  const blockingMessage = sessionError;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)] p-4">
      <Card className="w-full max-w-md border border-[var(--color-border-muted)] bg-[var(--color-surface)] p-6">
        <p className="font-[var(--font-display)] text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-highlight-blue)]">
          Fit2Lift account
        </p>
        <h1 className="mt-2 font-[var(--font-display)] text-xl font-bold uppercase tracking-[0.04em] text-[var(--color-text)]">
          Reset your password
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-muted)]">
          Choose a new password for your Fit2Lift login. This link expires after a short time.
        </p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          {saved ? (
            <Alert variant="success" role="status">
              Password updated. Taking you to your workspace…
            </Alert>
          ) : null}

          {blockingMessage ? (
            <Alert variant="error" role="alert">
              {blockingMessage}
            </Alert>
          ) : null}
          {error ? (
            <Alert variant="error" role="alert">
              {error}
            </Alert>
          ) : null}

          {!blockingMessage && !saved && (checking || !ready) ? (
            <p className="text-sm text-[var(--color-text-muted)]" role="status">
              Verifying reset link…
            </p>
          ) : null}

          {!saved ? (
            <div className={!ready || blockingMessage ? "pointer-events-none opacity-50" : ""}>
              <div>
                <Label htmlFor="recover-pw" className="text-[var(--color-text-subtle)]">
                  New password
                </Label>
                <Input
                  id="recover-pw"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                  required
                  minLength={8}
                  className="mt-1"
                  disabled={!ready || Boolean(blockingMessage)}
                />
              </div>
              <div>
                <Label htmlFor="recover-pw2" className="text-[var(--color-text-subtle)]">
                  Confirm password
                </Label>
                <Input
                  id="recover-pw2"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(ev) => setConfirm(ev.target.value)}
                  required
                  minLength={8}
                  className="mt-1"
                  disabled={!ready || Boolean(blockingMessage)}
                />
              </div>

              <Button
                type="submit"
                loading={loading}
                className="mt-2 w-full justify-center"
                disabled={!ready || Boolean(blockingMessage)}
              >
                Save password
              </Button>
            </div>
          ) : null}
        </form>

        {!saved ? (
          <p className="mt-6 text-center text-sm text-[var(--color-text-muted)]">
            <Link to="/login" className="text-[var(--color-signal-blue)] hover:underline">
              Back to sign in
            </Link>
          </p>
        ) : null}
      </Card>
    </div>
  );
}
